import type { PlaceCatalogEntry } from '@my-fullstack-app/trpc-types';
import type { PlaceDetails } from '@my-fullstack-app/itinerary-types';

/**
 * @deprecated ADR-0044 — transitional. Translates a vendor-neutral
 * PlaceCatalogEntry back into the legacy Google-shaped PlaceDetails
 * that the (still unmigrated) `addPlace` store action and `Place`
 * persisted shape expect.
 *
 * Once the Place schema is migrated to neutral fields and `addPlace`
 * accepts a `PlaceCatalogEntry` directly, this entire module — and
 * its handful of callers in PlaceLookupRow, PlaceSearchModal,
 * MapPlaceSearch, /trips/[id]/add, useShowSuggestionToast — go away.
 *
 * For Google-source entries the conversion is lossless because we
 * encoded both id and photoToken from Google's own native fields. For
 * non-Google sources the placeId would be a non-Google opaque string;
 * downstream code that expects Google's API to resolve it would
 * silently fail. Today we only register the Google provider, so the
 * non-Google path isn't exercised — but be aware before adding new
 * providers without first migrating the Place schema.
 */
export function catalogEntryToPlaceDetails(entry: PlaceCatalogEntry): PlaceDetails {
  // The neutral id is `'<source>:<native>'`. For now we strip the
  // prefix and feed only the native part forward, since downstream
  // consumers (addPlace, photo proxy fallback, persisted Place.placeId)
  // assume a Google placeId shape.
  const idx = entry.id.indexOf(':');
  const placeId = idx > 0 ? entry.id.slice(idx + 1) : entry.id;

  // Photo token format from google-place-adapter is
  // '<source>:<placeId>|<photoRef>' (or just '<placeId>|<photoRef>'
  // for legacy unprefixed records). Pull the photoRef off so the
  // legacy PlaceDetails.photoRef field is populated.
  let photoRef: string | undefined;
  if (entry.photoToken) {
    const native = entry.photoToken.startsWith('google:')
      ? entry.photoToken.slice('google:'.length)
      : entry.photoToken;
    const sep = native.indexOf('|');
    photoRef = sep > 0 ? native.slice(sep + 1) : undefined;
  }

  return {
    placeId,
    name: entry.name,
    address: entry.address,
    lat: entry.lat,
    lng: entry.lng,
    phone: entry.phone,
    website: entry.website,
    photoRef,
    photoAttribution: entry.photoAttribution,
    // PlaceDetails carries `types: string[]` (Google taxonomy); the
    // neutral entry has `category: PlaceCategory` (small bounded set).
    // We don't try to invert the mapping — leaving `types` undefined
    // is safer than producing a stub like ['lodging'] that downstream
    // code might treat as a Google type.
    openingHours: entry.openingHours,
  };
}
